import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/data-table';
import { useBudget, BudgetTransaction } from '@/hooks/useBudget';
import { formatCurrency, cn } from '@/lib/utils';
import {
  ArrowLeftRight,
  FileText,
  Package,
  Plus,
  Minus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function BudgetTransactionsPage() {
  const navigate = useNavigate();
  const { getBudgetTransactions } = useBudget();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data, isLoading, error, refetch } = getBudgetTransactions({
    page,
    page_size: pageSize,
  });

  const transactions: BudgetTransaction[] = data?.transactions || [];
  const totalCount = data?.total || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const filteredTransactions = typeFilter === 'all' 
    ? transactions 
    : transactions.filter(t => t.transaction_type === typeFilter);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'requisition_approved':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'po_created':
        return <FileText className="h-4 w-4 text-blue-600" />;
      case 'po_received':
        return <Package className="h-4 w-4 text-purple-600" />;
      case 'po_cancelled':
        return <Minus className="h-4 w-4 text-red-600" />;
      case 'adjustment':
        return <ArrowLeftRight className="h-4 w-4 text-yellow-600" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'requisition_approved':
        return 'Requisition Approved';
      case 'po_created':
        return 'PO Created';
      case 'po_received':
        return 'PO Received';
      case 'po_cancelled':
        return 'PO Cancelled';
      case 'adjustment':
        return 'Adjustment';
      default:
        return type;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'requisition_approved':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'po_created':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'po_received':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'po_cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'adjustment':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const columns = [
    {
      header: 'Date',
      accessor: (row: BudgetTransaction) => (
        <span className="text-sm">
          {format(new Date(row.created_at), 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      header: 'Time',
      accessor: (row: BudgetTransaction) => (
        <span className="text-sm text-text-muted">
          {format(new Date(row.created_at), 'HH:mm')}
        </span>
      ),
    },
    {
      header: 'Type',
      accessor: (row: BudgetTransaction) => (
        <div className="flex items-center gap-2">
          {getTransactionIcon(row.transaction_type)}
          <Badge className={getTransactionColor(row.transaction_type)}>
            {getTransactionLabel(row.transaction_type)}
          </Badge>
        </div>
      ),
    },
    {
      header: 'Description',
      accessor: (row: BudgetTransaction) => (
        <span className="text-sm max-w-xs truncate" title={row.description}>
          {row.description}
        </span>
      ),
    },
    {
      header: 'Amount',
      accessor: (row: BudgetTransaction) => (
        <span className={cn(
          'font-medium',
          row.transaction_type === 'po_cancelled' ? 'text-red-600' : 'text-green-600'
        )}>
          {row.transaction_type === 'po_cancelled' ? '-' : '+'}{formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      header: 'Reference',
      accessor: (row: BudgetTransaction) => (
        <div className="flex gap-1">
          {row.requisition_id && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => navigate(`/requisitions/${row.requisition_id}`)}
            >
              REQ #{row.requisition_id.slice(0, 8)}
            </Button>
          )}
          {row.purchase_order_id && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => navigate(`/purchase-orders/${row.purchase_order_id}`)}
            >
              PO #{row.purchase_order_id.slice(0, 8)}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const summaryStats = {
    totalAllocated: transactions
      .filter(t => t.transaction_type !== 'po_cancelled')
      .reduce((sum, t) => sum + t.amount, 0),
    totalCancelled: transactions
      .filter(t => t.transaction_type === 'po_cancelled')
      .reduce((sum, t) => sum + t.amount, 0),
    totalTransactions: transactions.length,
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Budget Transactions
          </h1>
          <p className="text-text-secondary mt-1">
            View all budget-related transactions and movements
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/budget')}>
            <ArrowLeftRight className="mr-2 h-4 w-4" />
            Back to Overview
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading transactions</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-text-secondary">Total Allocated</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {formatCurrency(summaryStats.totalAllocated)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-text-secondary">Total Cancelled</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              {formatCurrency(summaryStats.totalCancelled)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-text-secondary">Total Transactions</div>
            <div className="text-2xl font-bold text-text-primary mt-1">
              {summaryStats.totalTransactions}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <span className="text-sm font-medium text-text-secondary">Filters:</span>
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="requisition_approved">Requisition Approved</SelectItem>
                <SelectItem value="po_created">PO Created</SelectItem>
                <SelectItem value="po_received">PO Received</SelectItem>
                <SelectItem value="po_cancelled">PO Cancelled</SelectItem>
                <SelectItem value="adjustment">Adjustment</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredTransactions.length > 0 ? (
            <>
              <DataTable
                columns={columns}
                data={filteredTransactions}
                emptyMessage="No transactions found"
              />
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-text-muted">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={pageSize.toString()} onValueChange={(v) => {
                      setPageSize(parseInt(v));
                      setPage(1);
                    }}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 / page</SelectItem>
                        <SelectItem value="20">20 / page</SelectItem>
                        <SelectItem value="50">50 / page</SelectItem>
                        <SelectItem value="100">100 / page</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-text-muted">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <ArrowLeftRight className="h-12 w-12 mx-auto text-text-muted mb-3" />
              <p className="text-text-muted">No budget transactions yet</p>
              <p className="text-sm text-text-muted mt-1">
                Transactions will appear here when requisitions are approved or POs are created
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
